import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import type { InsuranceCostsResponse } from '../../types/admin';

interface InsuranceCostsTabProps {
  data: InsuranceCostsResponse;
}

const formatCost = (cost: number | null): string => {
  if (cost === null || cost === undefined) return 'N/A';
  return `$${cost.toFixed(4)}`;
};

const formatTokens = (tokens: number): string => {
  return tokens.toLocaleString();
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'completed': return 'success';
    case 'processing': return 'warning';
    case 'failed': return 'error';
    default: return 'default';
  }
};

const InsuranceCostsTab: React.FC<InsuranceCostsTabProps> = ({ data }) => {
  return (
    <Box>
      {/* Totals summary */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Totals: {data.totals.count} reports |{' '}
          {formatTokens(data.totals.totalTokens)} tokens |{' '}
          Cost: {formatCost(data.totals.totalCost)} (Extraction: {formatCost(data.totals.extractionCost)}, Standardization: {formatCost(data.totals.standardizationCost)})
        </Typography>
      </Paper>

      {/* Reports table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pet</TableCell>
              <TableCell>Breed</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">PDFs</TableCell>
              <TableCell align="right">Extraction</TableCell>
              <TableCell align="right">Standardization</TableCell>
              <TableCell align="right">Total Cost</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No insurance cost data available yet. Token tracking starts after deployment.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.reports.map((report) => (
                <TableRow key={report.reportId} hover>
                  <TableCell>
                    <Typography variant="body2">{report.petName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {report.breed}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={report.status} color={statusColor(report.status)} size="small" />
                  </TableCell>
                  <TableCell align="right">{report.pdfCount}</TableCell>
                  <TableCell align="right">{formatCost(report.costs.extraction)}</TableCell>
                  <TableCell align="right">{formatCost(report.costs.standardization)}</TableCell>
                  <TableCell align="right">{formatCost(report.costs.total)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {formatDate(report.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InsuranceCostsTab;
